import PropTypes from 'prop-types';
import React from 'react';
import { findDOMNode } from 'react-dom';
import { UX2, constants } from '@wsb/guac-widget-core';
import { OlsConfigStore, RouteHandler, ShopViewConstants, ScrollWidgetActions } from '@wsb/guac-widget-shared/lib/common/ols-core/core-bundle';
import dataAids from '../../../constants/dataAids';
import ErrorMessage from '../ErrorMessage';

class CategoryList extends React.Component {
  constructor() {
    super(...arguments);
    this.handleCategoryClick = this.handleCategoryClick.bind(this);
    this.toggleMobileList = this.toggleMobileList.bind(this);
    this.state = { showMobileList: false, limitContainer: false };
  }

  componentDidMount() {
    this.calculateCatHeight();
  }

  UNSAFE_componentWillReceiveProps() {
    this.calculateCatHeight();
  }

  calculateCatHeight() {
    this.desktopInnerNode = findDOMNode(this.desktopInnerRef);
    if (this.desktopInnerNode) {
      const listHeight = this.desktopInnerNode.offsetHeight;
      const containerHeight = this.desktopInnerNode.parentElement.offsetHeight;
      this.setState({ limitContainer: listHeight > containerHeight });
    }
  }

  scrollMobileToTop() {
    const mobileInnerNode = findDOMNode(this.mobileInnerRef);
    if (mobileInnerNode) {
      mobileInnerNode.scrollTop = 0;
    }
  }

  handleCategoryClick(permalink, isAllProducts, e) {
    e.preventDefault();
    const { currentSortOption } = this.props;
    const data = { taxonPermalink: permalink };
    if (currentSortOption) {
      data.sortOption = currentSortOption;
    }
    if (isAllProducts) {
      data.routePath = RouteHandler.ALL_PRODUCTS_ROUTE;
    }
    RouteHandler.navigate(ShopViewConstants.PRODUCT_LISTING, data);
    ScrollWidgetActions.scrollShopWidget();
    this.setState({ showMobileList: false });
    this.scrollMobileToTop();
  }

  toggleMobileList(e) {
    e.preventDefault();
    const { showMobileList } = this.state;
    this.scrollMobileToTop();
    this.setState({ showMobileList: !showMobileList });
  }

  buildLink({ name, permalink, isSelected, isAllProducts = false }) {
    const { renderMode } = this.props;
    const dataAid = `${dataAids.CATEGORY_LINK}_${permalink}`;
    const params = {};
    if (isAllProducts) {
      params.routePath = RouteHandler.ALL_PRODUCTS_ROUTE;
    } else {
      params.taxonPermalink = permalink;
    }

    const linkProps = this.context.getLinkProps(ShopViewConstants.PRODUCT_LISTING, params);
    const { Link } = UX2.Element;
    const styles = {
      display: 'block',
      font: 'alt',
      fontSize: 'medium',
      fontColor: isSelected ? 'highlight' : 'highContrast',
      fontWeight: isSelected ? 'bold' : 'normal',
      textWhitespace: 'normal',
      pointerEvents: isSelected ? 'none' : 'auto'
    };
    const isEditMode = renderMode === constants.renderModes.EDIT;
    return (
      <Link
        data-aid={ dataAid }
        onClick={ !isEditMode && this.handleCategoryClick.bind(null, permalink, isAllProducts) }
        children={ name }
        href={ (linkProps && linkProps.href) || 'javascript:;' }
        { ...linkProps }
        style={ styles }
      />
    );
  }

  buildList(taxons, isNested = false) {
    const translate = this.context.translate || (() => {});
    const { selectedTaxonPermalink, isSearch, routePath } = this.props;
    const { Block } = UX2.Element;
    const allProductsInfo = OlsConfigStore.getAllProductsInfo() || {};
    const defaultCategory = OlsConfigStore.getDefaultCategory();
    const allProductsPosition = allProductsInfo && 'position' in allProductsInfo ? allProductsInfo.position : 0;
    const shouldHighlightDefault = allProductsPosition !== 0 && routePath === RouteHandler.PRODUCTS_ROUTE && !selectedTaxonPermalink;

    const buildViewAllLi = () => {
      const name = allProductsInfo.name || translate('products.view_all');
      // 'All Products' should only be selected when the following conditions are true:
      // 1. There is no selected permalink
      // 2. We're not rendering search results
      // 3. List position is 0 -or- routePath is specifically 'all'
      const isSelected = !(selectedTaxonPermalink || isSearch) &&
                          (allProductsPosition === 0 || routePath === RouteHandler.ALL_PRODUCTS_ROUTE);

      return (
        <Block
          key={ 'view-all' }
          data-aid={ dataAids.CATEGORY_VIEW_ALL }
          style={{ marginTop: 'medium' }}
          children={ this.buildLink({
            name,
            permalink: null,
            isSelected,
            isAllProducts: true
          }) }
        />
      );
    };

    const getSubcategoryPermalinks = taxon => {
      let subcategoryPermalinks = [];
      taxon.taxons.forEach(subTaxon => {
        if (subTaxon.taxons.length > 0) {
          subcategoryPermalinks = subcategoryPermalinks.concat(
            getSubcategoryPermalinks(subTaxon)
          );
        }
        subcategoryPermalinks.push(subTaxon.permalink);
      });
      return subcategoryPermalinks;
    };

    const shouldShowSubcategories = taxon => {
      if (taxon.permalink === selectedTaxonPermalink) return true;
      const subcategoryPermalinks = getSubcategoryPermalinks(taxon);
      return subcategoryPermalinks.indexOf(selectedTaxonPermalink) > -1;
    };

    const styles = {
      paddingLeft: isNested ? 'small' : 'none',
      paddingRight: 'medium'
    };

    const taxonList = taxons.map(taxon => {
      return (
        <Block key={ taxon.id } style={{ marginTop: 'medium' }}>
          {this.buildLink({
            name: taxon.name,
            permalink: taxon.permalink,
            isSelected: (taxon.permalink === selectedTaxonPermalink) || (shouldHighlightDefault && taxon.permalink === defaultCategory)
          })}
          {taxon.taxons.length > 0 && shouldShowSubcategories(taxon)
            ? this.buildList(taxon.taxons, true)
            : null}
        </Block>
      );
    });

    // Insert 'All Products' link
    if (!isNested) {
      const position = allProductsInfo.position || 0;
      taxonList.splice(position, 0, buildViewAllLi());
    }

    return (
      <Block style={ styles } children={ taxonList } />
    );
  }

  render() {
    const translate = this.context.translate || (() => {});
    let categoryData;
    const { showMobileList, limitContainer } = this.state;
    const { taxons } = this.props;
    const listHeight = this.desktopInnerNode ? this.desktopInnerNode.offsetHeight : 0;
    const { Block, Link, HR, Icon } = UX2.Element;

    if (taxons && taxons.length) {
      categoryData = this.buildList(taxons);
    } else {
      categoryData = (
        <Block>
          <ErrorMessage message={ translate('categories.error_fetching_category_data') } />
        </Block>
      );
    }
    const styles = {
      outer: {
        position: 'relative',
        minHeight: listHeight < 400 ? listHeight + 40 + 'px' : '400px',
        flex: 1,
        width: '100%'
      },
      innerDesktop: {
        'position': 'absolute',
        'width': '100%',
        'top': '0px',
        'bottom': '0px',
        '@xs-only': {
          display: 'none'
        }
      },
      listOuter: {
        marginTop: '-medium',
        position: 'absolute',
        top: '0',
        bottom: '0px',
        width: '100%',
        overflow: 'auto',
        borderBottomWidth: limitContainer ? '1px' : '0',
        borderBottomStyle: limitContainer ? 'solid' : 'none',
        borderBottomColor: 'section'
      },
      listInner: {
        position: 'relative',
        paddingBottom: 'medium'
      },
      fadeTop: {
        position: 'absolute',
        bottom: '4px',
        display: limitContainer ? 'block' : 'none',
        width: '100%',
        height: '6px',
        backgroundColor: 'section',
        opacity: '0.4'
      },
      fadeBottom: {
        position: 'absolute',
        bottom: '1px',
        display: limitContainer ? 'block' : 'none',
        width: '100%',
        height: '3px',
        backgroundColor: 'section',
        opacity: '0.6'
      },
      innerMobile: {
        'position': 'relative',
        'flexDirection': 'column',
        '@sm': {
          display: 'none'
        }
      },
      toggleOuter: {
        margin: '0',
        fontSize: 'large',
        fontColor: 'highContrast'
      },
      toggleInner: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      },
      listOuterMobile: {
        'position': 'relative',
        'width': '100%',
        'maxHeight': '50vh',
        'overflow': 'auto',
        '@xs-only': {
          display: showMobileList ? 'block' : 'none'
        }
      },
      listInnerMobile: {
        display: 'table',
        width: '100%',
        paddingBottom: 'medium'
      },
      hr: {
        marginTop: '0',
        marginBottom: 'large'
      }
    };
    return (
      <Block style={ styles.outer }>
        <Block style={ styles.innerDesktop }>
          <Block style={ styles.listOuter }>
            <Block
              ref={ ref => {
                this.desktopInnerRef = ref;
              } }
              children={ categoryData }
              style={ styles.listInner }
            />
          </Block>
          <Block style={ styles.fadeTop } />
          <Block style={ styles.fadeBottom } />
        </Block>
        <Block style={ styles.innerMobile }>
          <Link
            onClick={ this.toggleMobileList }
            href='javascript:;'
            data-aid={ dataAids.TOGGLE_MOBILE_LIST }
            style={ styles.toggleOuter }
          >
            <Block style={ styles.toggleInner }>
              <Block
                children={ translate('categories.categories') }
                style={{ marginBottom: 'xsmall' }}
              />
              <Icon
                icon='chevronDown'
                size='xsmall'
                rotate={ showMobileList ? '-180' : false }
                style={{ marginLeft: 'xxsmall' }}
              />
            </Block>
          </Link>
          <Block
            ref={ ref => {
              this.mobileInnerRef = ref;
            } }
            style={ styles.listOuterMobile }
          >
            <Block children={ categoryData } style={ styles.listInnerMobile } />
          </Block>
          <HR style={ styles.hr } />
        </Block>
      </Block>
    );
  }
}

CategoryList.propTypes = {
  taxons: PropTypes.array,
  selectedTaxonPermalink: PropTypes.string,
  isSearch: PropTypes.bool,
  currentSortOption: PropTypes.string,
  renderMode: PropTypes.string,
  routePath: PropTypes.any
};

CategoryList.contextTypes = {
  getLinkProps: PropTypes.func,
  translate: PropTypes.func
};

export default CategoryList;
